import { memo } from 'react';
import { TrendingDown } from 'lucide-react';
import { Tooltip, KPI_TOOLTIPS } from '@/components/ui';
import type { HistoricalFunnelData } from '@/utils/analytics';
import type { Lead } from '@/types';
import type { AnalyticsDetailData } from './AnalyticsDetailModal';

interface ConversionFunnelChartProps {
  historicalData?: HistoricalFunnelData | null;
  leads?: Lead[];
  isLoading?: boolean;
  onStageClick?: (data: AnalyticsDetailData) => void;
}

export const ConversionFunnelChart = memo(function ConversionFunnelChart({
  historicalData,
  leads = [],
  isLoading,
  onStageClick,
}: ConversionFunnelChartProps) {
  const historicalMaxCount = historicalData
    ? Math.max(...historicalData.stages.map((s) => s.passedThrough), 1)
    : 1;

  const handleStageClick = (stage: HistoricalFunnelData['stages'][0]) => {
    if (!onStageClick) return;

    // Filter leads by IDs that passed through this stage historically
    const passedThroughIds = new Set(stage.passedThroughIds || []);
    const stageLeads = leads.filter((l) => passedThroughIds.has(l.id));

    onStageClick({
      title: `${stage.stage} - Historical Journey`,
      subtitle: `${stage.passedThrough} leads passed through (${stage.currentlyIn} currently here)`,
      leads: stageLeads,
    });
  };

  // Check if we have meaningful data
  const hasData = historicalData && historicalData.stages.length > 0 &&
    historicalData.stages.some(s => s.passedThrough > 0);

  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingDown className="h-5 w-5 text-[#F78C4E]" />
          <Tooltip {...KPI_TOOLTIPS.conversionFunnel}>
            <h3 className="text-lg font-semibold text-white">Conversion Funnel</h3>
          </Tooltip>
        </div>
      </div>
      <p className="text-xs text-zinc-500 mb-4">
        Historical journey of leads through pipeline stages
      </p>

      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-zinc-600 border-t-[#F78C4E]" />
        </div>
      ) : !hasData ? (
        <div className="text-center py-6 space-y-2">
          <p className="text-zinc-400">No historical stage transitions available</p>
          <p className="text-xs text-zinc-500">
            Historical funnel requires stage change history from Monday.com activity logs.
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Summary stats */}
          <div className="flex items-center justify-between text-xs text-zinc-500 mb-3 pb-2 border-b border-zinc-800">
            <span>{historicalData.totalLeadsInPeriod} leads tracked</span>
            <span className="text-emerald-400">{historicalData.overallConversionRate}% overall conversion</span>
          </div>

          {historicalData.stages.map((stage, index) => {
            const widthPercent = (stage.passedThrough / historicalMaxCount) * 100;
            const isLast = index === historicalData.stages.length - 1;
            const nextStage = historicalData.stages[index + 1];

            // Skip stages with no leads
            if (stage.passedThrough === 0) return null;

            return (
              <div key={stage.stage}>
                <button
                  onClick={() => handleStageClick(stage)}
                  className="w-full flex items-center gap-3 rounded-lg p-1 -m-1 hover:bg-zinc-800/50 transition-colors cursor-pointer"
                >
                  <span className="w-28 text-sm text-zinc-400 truncate text-left" title={stage.stage}>
                    {stage.stage}
                  </span>
                  <div className="flex-1 flex items-center justify-center">
                    <div
                      className="h-8 bg-gradient-to-r from-[#F78C4E]/80 to-[#E67A3C]/80 rounded flex items-center justify-center transition-all duration-500 hover:from-[#F78C4E] hover:to-[#E67A3C] relative"
                      style={{
                        width: `${Math.max(widthPercent, 20)}%`,
                      }}
                    >
                      <span className="text-sm font-semibold text-white">
                        {stage.passedThrough}
                      </span>
                      {stage.currentlyIn > 0 && stage.currentlyIn !== stage.passedThrough && (
                        <span className="absolute -right-1 -top-1 bg-emerald-500 text-[10px] text-white px-1 rounded">
                          {stage.currentlyIn}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="w-20 text-right">
                    <span className="text-xs text-zinc-500">{stage.avgDaysInStage}d avg</span>
                  </div>
                </button>
                {!isLast && nextStage && nextStage.passedThrough > 0 && (
                  <div className="flex items-center gap-3 py-1">
                    <span className="w-28" />
                    <div className="flex-1 flex justify-center gap-3">
                      <span className="text-xs text-emerald-400">
                        {stage.conversionRate}% progressed
                      </span>
                      {stage.dropoffRate > 0 && (
                        <span className="text-xs text-red-400">
                          {stage.dropoffRate}% dropped
                        </span>
                      )}
                    </div>
                    <span className="w-20" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
});
